import React, { useState, useEffect, useCallback, useRef } from 'react';
import { sendAuthenticatedRequest } from '../utils/api'; // Corrected import path
import socket from '../utils/socket';
import './MessagesSideDrawer.css';
import Panel from './Panel';
import Avatar from './Avatar';
import VoiceMessage from './VoiceMessage';

// Helper function to format the timestamp
// What a conversation LIST needs, as opposed to what a message needs. The full
// "20/09/2026, 12:14 am" is right against a single message and wrong against a row that also has to
// carry a name and a line of text in 340px: it took a third of the width to say something nobody
// reads precisely. Today gives a time, this week a day, older a date — the resolution people
// actually want, which falls off with age.
const shortTimestamp = (timestamp) => {
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const days = Math.floor((now - d) / 86400000);
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

// 2026-10-18: was a full "16/08/2026, 06:46 pm" under EVERY message, in a size close to the
// message itself. That made every message two lines tall and repeated the date dozens of times in
// one afternoon's conversation — which is the empty space that got reported.
//
// A thread needs two different things, and they are not the same thing: WHICH DAY, said once when
// it changes, and WHAT TIME, said at the end of a run of messages. The phone shows neither (see
// ConversationScreen.js — bubbles and a read receipt, no timestamps); a wide screen has the room
// for the time, so this keeps it, quietly.
const timeOf = (ts) => {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const dayKeyOf = (ts) => {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toDateString();
};

// "Today" and "Yesterday" rather than the date, for the two days people are usually reading about.
const dayLabelOf = (ts) => {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString([], sameYear
    ? { weekday: 'short', day: 'numeric', month: 'long' }
    : { day: 'numeric', month: 'long', year: 'numeric' });
};

// Who sent it and when, turned into the three things the markup needs: a day divider above it when
// the day changed, whether it continues the message above (same sender, close in time), and
// whether it ENDS a run — which is the only message in a run that shows a time.
const GROUP_GAP_MS = 5 * 60 * 1000;
const decorate = (messages, currentUserId) => messages.map((msg, i) => {
  const prev = messages[i - 1];
  const next = messages[i + 1];
  const mine = msg.sender_id === currentUserId;
  const t = new Date(msg.created_at).getTime();
  const sameSenderAsPrev = prev && (prev.sender_id === currentUserId) === mine;
  const sameSenderAsNext = next && (next.sender_id === currentUserId) === mine;
  return {
    msg,
    mine,
    dayLabel: (!prev || dayKeyOf(prev.created_at) !== dayKeyOf(msg.created_at))
      ? dayLabelOf(msg.created_at) : null,
    continues: !!sameSenderAsPrev && Math.abs(t - new Date(prev.created_at).getTime()) < GROUP_GAP_MS
      && dayKeyOf(prev.created_at) === dayKeyOf(msg.created_at),
    endsRun: !sameSenderAsNext
      || Math.abs(new Date(next.created_at).getTime() - t) >= GROUP_GAP_MS
      || dayKeyOf(next.created_at) !== dayKeyOf(msg.created_at),
  };
});


// openWith: a person to open the conversation with directly, rather than making the reader find
// them in the list. 2026-10-18: this is what the ✉ on a profile card now does. It used to open
// ChatSideDrawer — a SECOND chat surface, in the old purple visual language, on the opposite side
// of the screen, showing the same messages as this one. Both could be on screen at once, and were.
const MessagesSideDrawer = ({ isOpen, onClose, allChatMessages, unreadMessages, currentUserId, clearUnreadMessages, openWith, onOpened, onOpenProfile }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  // State for chat input and handlers
  const [chatInput, setChatInput] = useState('');
  const historyRef = useRef(null);

  const handleConversationSelect = async (otherUser) => {
    const convo = conversations.find(c => c.otherUser.id === otherUser.id);
    const unreadCount = convo ? convo.unreadCount : 0;

    if (clearUnreadMessages) {
      clearUnreadMessages(otherUser.id);
    }

    setSelectedConversation(otherUser);
    try {
      // 2026-08-29: server now returns { messages, hasMore } (cursor-paginated, most recent page)
      // instead of a bare array — see server/index.js. hasMore unused here for now, same as App.js.
      const { messages } = await sendAuthenticatedRequest(`/messages/conversations/${otherUser.id}`);
      const messagesWithReadStatus = (messages || []).map((msg, index) => ({
        ...msg,
        is_new: index >= messages.length - unreadCount,
      }));
      setMessageHistory(messagesWithReadStatus);
    } catch (error) {
      console.error('Error fetching message history:', error);
    }
  };

  // Opening on a named person. handleConversationSelect does the fetching and the unread-clearing,
  // so this only has to decide WHEN — and then tell the parent it has, so the same person asked
  // for twice in a row still opens the second time.
  useEffect(() => {
    if (!isOpen || !openWith?.id) return;
    handleConversationSelect(openWith);
    onOpened?.();
    // handleConversationSelect is redefined every render and is not a dependency anybody wants
    // here: this must run when the REQUEST changes, not when the component re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, openWith?.id]);

  // Scroll to the newest message. A thread that opens at the TOP shows the oldest thing in it,
  // which for a conversation is the least useful end — and a message you have just sent appearing
  // below the fold reads as a message that did not send. Not smooth on open: arriving part-way
  // through an animated scroll is its own kind of disorienting.
  useEffect(() => {
    const el = historyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messageHistory, selectedConversation]);

  // "Delete for everyone" (2026-10-27): deleted on the phone, by either person — the bubble here
  // becomes the placeholder where it stands, as it does on the phone.
  useEffect(() => {
    const onMessageDeleted = (data) => {
      if (!data?.id) return;
      setMessageHistory((prev) => prev.map((msg) => (
        msg.id === data.id ? { ...msg, message: null, deleted_at: data.deletedAt || new Date().toISOString() } : msg
      )));
    };
    socket.on('messageDeleted', onMessageDeleted);
    return () => socket.off('messageDeleted', onMessageDeleted);
  }, []);

  useEffect(() => {
    if (messageHistory.some(msg => msg.is_new)) {
      const timer = setTimeout(() => {
        setMessageHistory(prevHistory =>
          prevHistory.map(msg => ({ ...msg, is_new: false }))
        );
      }, 2000); // 2 seconds

      return () => clearTimeout(timer);
    }
  }, [messageHistory]);

  useEffect(() => {
    console.log('MessagesSideDrawer useEffect triggered.');
    console.log('isOpen:', isOpen, 'currentUserId:', currentUserId);
    console.log('allChatMessages:', allChatMessages);
    console.log('unreadMessages:', unreadMessages);

    if (isOpen && currentUserId) {
      const fetchConversations = async () => {
        try {
          console.log('Fetching conversations from /messages/conversations...');
          const fetchedData = await sendAuthenticatedRequest('/messages/conversations');
          console.log('Fetched data:', fetchedData);

          const combinedConversationsMap = new Map();

          // 1. Add all fetched conversations to the map
          fetchedData.forEach(convo => {
            combinedConversationsMap.set(convo.other_user_id, {
              otherUser: {
                id: convo.other_user_id,
                username: convo.other_username,
                // 2026-10-14: was 'https://via.placeholder.com/50'. Avatar already draws a local
                // fallback — the initial on a coloured disc — so a null is both correct and private;
                // the placeholder fetched a stranger's image host on every render and was blocked
                // by img-src anyway, which is the same CSP gap that greyed out the map.
                avatar_url: convo.other_avatar_url || null
              },
              lastMessage: convo.deleted_at ? 'Message deleted' : convo.message,
              timestamp: convo.created_at,
              unreadCount: unreadMessages[convo.other_user_id] || 0,
            });
          });
          console.log('After adding fetched data:', combinedConversationsMap);


          // 2. Iterate through allChatMessages and update/add to the map
          //    This ensures real-time messages are reflected and take precedence for the 'lastMessage' and 'timestamp'
          Object.entries(allChatMessages).forEach(([otherUserIdStr, messages]) => {
            const otherUserId = parseInt(otherUserIdStr, 10);
            if (messages.length > 0) {
              const lastMessage = messages[messages.length - 1];
              const existingConvo = combinedConversationsMap.get(otherUserId);

              // Determine username and avatar_url: prefer existing fetched data, otherwise derive from lastMessage
              const username = existingConvo?.otherUser.username || (lastMessage.from === currentUserId ? lastMessage.to_username : lastMessage.from_username);
              const avatar_url = existingConvo?.otherUser.avatar_url || null; // Avatar renders the initial when there is none

              combinedConversationsMap.set(otherUserId, {
                otherUser: {
                  id: otherUserId,
                  username: username,
                  avatar_url: avatar_url
                },
                lastMessage: lastMessage.message,
                timestamp: lastMessage.timestamp,
                unreadCount: unreadMessages[otherUserId] || 0,
              });
            }
          });
          console.log('After merging with allChatMessages:', combinedConversationsMap);


          // Sort conversations by timestamp (latest first)
          const sortedConversations = Array.from(combinedConversationsMap.values()).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          console.log('Sorted conversations:', sortedConversations);
          setConversations(sortedConversations);

        } catch (error) {
          console.error('Error in fetchConversations:', error);
        }
      };

      fetchConversations();
    } else if (isOpen && !currentUserId) {
      console.log('MessagesSideDrawer is open but currentUserId is not available.');
    } else if (!isOpen) {
      console.log('MessagesSideDrawer is closed.');
    }
  }, [isOpen, currentUserId, allChatMessages, unreadMessages]);


  const handleChatInputChange = useCallback((e) => {
    setChatInput(e.target.value);
  }, []);

  const handleSendMessage = useCallback(() => {
    if (chatInput.trim() && selectedConversation) {
      const newMessage = {
        from: currentUserId,
        to: selectedConversation.id,
        message: chatInput,
      };
      socket.emit('privateMessage', newMessage);
      // The render below reads sender_id/created_at (the DB row shape historical fetches use), not
      // from/timestamp — without this, the just-sent message showed "Invalid Date" and rendered on
      // the wrong ('received') side until the conversation was reloaded from history.
      setMessageHistory((prevHistory) => [...prevHistory, {
        ...newMessage,
        sender_id: currentUserId,
        created_at: new Date().toISOString(),
      }]);
      setChatInput('');
    }
  }, [chatInput, selectedConversation, currentUserId]);


  // 2026-10-15: this had its own shell — its own width, offset, radius, surface and transition —
  // as did four other drawers opening over the same map, and none of them agreed. The shell is
  // Panel now; what is left here is what is actually particular to messages.
  return (
    <Panel
      open={isOpen}
      title={selectedConversation ? selectedConversation.username : 'Messages'}
      onClose={onClose}
      onBack={selectedConversation ? () => setSelectedConversation(null) : undefined}
      // The name at the top of a conversation opens that person's card. Not the names in the LIST:
      // each of those rows is already a button that opens the conversation, and a control inside a
      // control is both invalid and ambiguous — the phone's RequestCard hit the same thing and
      // made the whole row one target rather than nesting two.
      onTitleClick={selectedConversation ? () => onOpenProfile?.(selectedConversation) : undefined}
      labelledBy="messages-panel-title"
      bodyClass={selectedConversation ? 'panel-body--column' : undefined}
    >
      {selectedConversation ? (
        <>
          <div className="message-history-container" ref={historyRef}>
            {decorate(messageHistory, currentUserId).map(({ msg, mine, dayLabel, continues, endsRun }, index) => (
              <React.Fragment key={index}>
                {dayLabel && <div className="msg-day">{dayLabel}</div>}
                <div
                  className={`msg-row${mine ? ' is-mine' : ''}${continues ? ' continues' : ''}${msg.is_new ? ' is-new' : ''}`}
                >
                  <div className={`msg-bubble${endsRun ? ' ends-run' : ''}${msg.deleted_at ? ' is-deleted' : ''}`}>
                    {msg.deleted_at
                      ? 'Message deleted'
                      : (msg.kind === 'voice' && msg.id ? <VoiceMessage id={msg.id} durationMs={msg.audio_ms} /> : msg.message)}
                  </div>
                  {/* Only at the end of a run: a time against every line of a rapid exchange is
                      five copies of the same minute. */}
                  {endsRun && <span className="msg-time">{timeOf(msg.created_at)}</span>}
                </div>
              </React.Fragment>
            ))}
          </div>
          {/* 2026-10-18: there was no composer. handleSendMessage and handleChatInputChange were
              written, correct, and never rendered — two "assigned but never used" warnings that
              had been in every build for days. That is why ChatSideDrawer was still alive: it was
              the only surface that could actually reply. It is gone, and this is where you reply. */}
          <form
            className="message-composer"
            onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
          >
            <input
              className="control control--sm"
              type="text"
              value={chatInput}
              onChange={handleChatInputChange}
              placeholder={`Message ${selectedConversation.username}…`}
              aria-label={`Message ${selectedConversation.username}`}
            />
            <button type="submit" className="btn btn--primary btn--sm" disabled={!chatInput.trim()}>
              Send
            </button>
          </form>
        </>
      ) : conversations.length === 0 ? (
        <p className="panel-empty">
          No messages yet.
          <br />
          A conversation starts when you request a space, or somebody requests yours.
        </p>
      ) : (
        conversations.map((convo) => (
          <button
            key={convo.otherUser.id}
            type="button"
            className="panel-row"
            onClick={() => handleConversationSelect(convo.otherUser)}
          >
            <Avatar username={convo.otherUser.username} avatarUrl={convo.otherUser.avatar_url} size={38} />
            <span className="panel-row-main">
              <span className="panel-row-title">{convo.otherUser.username}</span>
              <span className="panel-row-sub">{convo.lastMessage}</span>
            </span>
            <span className="panel-row-aside">{shortTimestamp(convo.timestamp)}</span>
            {convo.unreadCount > 0 && <span className="panel-count">{convo.unreadCount}</span>}
          </button>
        ))
      )}
    </Panel>
  );
};

export default MessagesSideDrawer;